<script setup lang="ts">
import { CustomersRatingTag } from '@apps/main/new-desktop/modules/customersRating';
import { HotelAmenities } from '@apps/main/new-desktop/modules/hotelAmenities';
import {
  useBrowserStore,
  useItineraryStore,
  useRouterStore,
} from '@apps/main/new-desktop/core/stores';
import { getBoardBasisText } from '@apps/main/new-desktop/core/helpers/translation.helper';
import { DealCodeTag } from '@apps/main/new-desktop/modules/dealCode';
import { BoardBasisTag } from '@apps/main/new-desktop/modules/boardBasisTag';

import { AppSvgIcon, AppRouterLink, AppStars } from '@libs/ui';

import type {
  HotelSelectionCardContentData,
  HotelSelectionCardContext,
  HotelSelectionCardSummaryData,
} from '../models';

interface HotelSelectionCardContentProps {
  data: HotelSelectionCardContentData;
  summaryData: HotelSelectionCardSummaryData;
  context: HotelSelectionCardContext;
}

const props = defineProps<HotelSelectionCardContentProps>();

interface HotelSelectionCardContentEmits {
  (eventName: 'peek-price'): void;
  (eventName: 'navigation-triggered'): void;
}

const emit = defineEmits<HotelSelectionCardContentEmits>();

const routerStore = useRouterStore();
const itineraryStore = useItineraryStore();
const browserStore = useBrowserStore();

// NOTE: This could be false when all days in month are cached in a single date search session. Then opening peek price calendar for a different hotel than the one included in the itinerary would result in Peek Price with not-searched dates.
const isSingleDateSearch = !itineraryStore.areWholeMonthItinerariesSearched;
</script>

<template>
  <div class="hotel-selection-card-content">
    <div class="hotel-details">
      <div class="hotel-base-info">
        <AppRouterLink
          :link="
            routerStore.getHotelDetailsUrlInHotelSelection(
              { ingredientIndex: context.ingredientIndex },
              context.hotelCode
            )
          "
          font="font-body-m-bold"
          color="color-neutral-70"
          class="name"
          @navigation-triggered="emit('navigation-triggered')"
        >
          {{ props.data.title }}
        </AppRouterLink>

        <div class="location">{{ props.data.subtitle }}</div>
        <div class="ratings">
          <AppStars class="stars" :amount="props.data.stars" size="18" />
          <CustomersRatingTag
            v-if="props.data.guestRating"
            :guest-rating="props.data.guestRating"
            font-size="s"
          />
        </div>
      </div>

      <HotelAmenities class="amenities" :amenities="props.data.amenities" />

      <BoardBasisTag
        v-if="props.data.boardBasis && props.data.boardBasis?.[0]"
        :board-basis-code="props.data.boardBasis[0]"
        :translations-callback="getBoardBasisText"
        class="board-basis-tag"
      />

      <div v-if="browserStore.isLargerThan600px" class="hotel-description">
        <ul class="nearby-places">
          <li v-for="content in props.data.content" :key="content" class="item">
            <span class="item-text">
              {{ content }}
            </span>
          </li>
        </ul>
      </div>
    </div>

    <div
      class="hotel-summary"
      :class="{ 'without-peek-price-button': isSingleDateSearch }"
    >
      <button
        v-if="!isSingleDateSearch"
        class="hotel-price-calendar"
        @click.stop="emit('peek-price')"
      >
        <div class="icon-wrapper">
          <AppSvgIcon name="lowPriceCalendar" color="color-white" size="18" />
        </div>

        <div class="hotel-price-calendar-text">
          <span>
            {{ $t('planner.low_price_calendar.label') }}
          </span>
          <span class="cta-label">
            {{ $t('planner.low_price_calendar.action') }}
          </span>
        </div>
      </button>

      <div class="hotel-price-section">
        <div class="hotel-deal-description">
          <div class="total-price-label only-mobile">
            {{ $t('planner.price_for_this_stay') }}
          </div>
          <DealCodeTag
            v-if="props.summaryData.grossPrice"
            :wrap="true"
            type="tag"
            class="campaign-code-tag"
          />
        </div>
        <div class="hotel-price-description">
          <div class="hotel-price">
            <div v-if="props.summaryData.grossPrice" class="gross-price">
              {{ props.summaryData.grossPrice }}
            </div>
            <div
              class="total-price"
              :class="{
                'with-discount': props.summaryData.grossPrice,
              }"
            >
              {{ props.summaryData.totalPrice }}
            </div>
          </div>
          <div class="total-price-label only-desktop">
            {{ $t('planner.price_for_this_stay') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.hotel-selection-card-content {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  container-type: inline-size;
  container-name: hotelCard;

  .hotel-details {
    display: flex;
    gap: var(--spacing-12);
    flex-direction: column;

    .hotel-base-info {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-4);

      .name {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .location {
        @include font-body-s-light;
        text-transform: capitalize;
        color: var(--color-neutral-70);
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .ratings {
        display: flex;
        gap: var(--spacing-12);
        padding-top: var(--spacing-4);
        align-items: center;
      }
    }

    .hotel-description {
      padding-top: var(--spacing-4);
      padding-bottom: var(--spacing-4);
      ul {
        margin: 0;
        padding-left: var(--spacing-16);
        list-style: none;

        .item {
          @include font-label-s-light;
          color: var(--color-neutral-70);
          position: relative;
          // aliign to start of text above (18x wide icon + 4px gap)
          padding-left: 6px;

          // NOTE: Standard list-style is broken on mobile,
          // when using overflow + ellipsis on list element.
          // Hence custom dot element
          &::before {
            content: '';
            width: 3px;
            height: 3px;
            border-radius: 50%;
            position: absolute;
            background: var(--color-neutral-70);
            left: -9px;
            top: 50%;
            transform: translateY(-50%);
          }

          .item-text {
            display: -webkit-box;
            -webkit-line-clamp: 1;
            line-clamp: 1;
            -webkit-box-orient: vertical;
            word-break: break-all;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          &:not(:last-child) {
            margin-bottom: var(--spacing-4);
          }
        }
      }
    }

    .board-basis-tag {
      padding-bottom: var(--spacing-8);
    }
  }

  .hotel-summary {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
    gap: var(--spacing-8);
    border-top: 1px dashed var(--color-neutral-40);
    padding-top: var(--spacing-12);

    &.without-peek-price-button {
      display: block;
    }

    @container hotelCard (max-width: 400px) {
      flex-direction: column;
      gap: var(--spacing-12);
      padding-top: 0;
      border-top: none;
    }

    .hotel-price-calendar {
      all: unset;
      display: flex;
      align-items: center;
      gap: var(--spacing-8);
      padding: var(--spacing-8);

      color: var(--color-neutral-70);
      background-color: var(--color-information-light);
      border-radius: var(--border-radius-8);
      border: 1px solid var(--color-brand-lilac);

      cursor: pointer;

      @include font-label-m-bold;

      @container hotelCard (max-width: 400px) {
        width: calc(100% - var(--spacing-16));
      }

      .icon-wrapper {
        background-color: var(--color-brand-violet);
        padding: var(--spacing-4);
        border-radius: var(--border-radius-8);
      }

      .hotel-price-calendar-text {
        display: flex;
        flex-direction: column;
        text-align: left;
        span {
          @include font-label-s-light;
          color: var(--color-neutral-70);
        }
        .cta-label {
          @include font-label-s-bold;
          text-decoration: underline;
        }
      }

      @include on-hover {
        background-color: var(--color-neutral-10);

        .cta-label {
          color: var(--color-brand-violet);
        }
      }
    }

    .hotel-price-section {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-4);

      .only-mobile {
        display: none;
      }
      .only-desktop {
        display: block;
      }
      @container hotelCard (max-width: 400px) {
        width: 100%;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;

        .only-mobile {
          display: block;
        }
        .only-desktop {
          display: none;
        }
      }

      .total-price-label {
        padding-bottom: var(--spacing-4);
        @include font-label-s-light;
        color: var(--color-neutral-70);
        text-align: right;
      }

      .hotel-deal-description {
        .total-price-label {
          text-align: left;
        }
        .campaign-code-tag {
          flex-direction: row;
          align-items: center;
        }
      }

      .hotel-price-description {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-4);
        align-items: flex-end;
        .hotel-price {
          display: flex;
          align-items: center;
          gap: var(--spacing-4);

          @container hotelCard (max-width: 400px) {
            width: 100%;
            flex-direction: column;
            align-items: flex-end;
          }
          .gross-price {
            @include font-body-m-bold;
            color: var(--color-neutral-70);
            text-decoration: line-through;
          }

          .total-price {
            @include font-heading-6;
            color: var(--color-neutral-70);

            &.with-discount {
              color: var(--color-error-dark);
            }
          }
        }
      }
    }
  }
}
</style>
